import {interpolateRoutePath} from '../shared/route-interpolation.js';
import type { LocalPosition, LocalRoutePoint } from "./local-api.ts";
import { trackerMapModel } from "./tracker-map-model.ts";

export function localDateKey(value = new Date()) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}
export function routeDayBounds(key: string) {
  const match = key.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match || Number(match[1]) < 1970) return null;
  const start = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (!Number.isFinite(start.getTime()) || localDateKey(start) !== key) return null;
  const end = new Date(start); end.setDate(end.getDate() + 1);
  return { startAt: start.toISOString(), endAt: end.toISOString() };
}
export function shiftedDateKey(key: string, days: number) {
  const bounds = routeDayBounds(key); if (!bounds) return localDateKey();
  const value = new Date(bounds.startAt); value.setDate(value.getDate() + days); return localDateKey(value);
}

type Coordinate = { latitude: number; longitude: number };
const radians = (degrees: number) => degrees * Math.PI / 180;
function distanceMeters(a: Coordinate, b: Coordinate) {
  const h = Math.sin(radians(b.latitude - a.latitude) / 2) ** 2 +
    Math.cos(radians(a.latitude)) * Math.cos(radians(b.latitude)) * Math.sin(radians(b.longitude - a.longitude) / 2) ** 2;
  return 6371000 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, h))));
}
const isCoordinate = (point: unknown): point is Coordinate => {
  if (!point || typeof point !== "object") return false;
  const p = point as Coordinate;
  return Number.isFinite(p.latitude) && Math.abs(p.latitude) <= 90 && Number.isFinite(p.longitude) && Math.abs(p.longitude) <= 180;
};

// A visual estimate only: never add these coordinates to observations or metrics.
// Long absences, reversed clocks and implausible jumps remain separate trips.
function estimatedConnection(previous: LocalRoutePoint | undefined, current: LocalRoutePoint) {
  if (!previous || current.connectFromPrevious) return null;
  const from = trackerMapModel(previous).point, to = trackerMapModel(current).point;
  const seconds = (Date.parse(current.receivedAt) - Date.parse(previous.receivedAt)) / 1000;
  if (!from || !to || !(seconds > 0 && seconds <= 900) || distanceMeters(from, to) / seconds * 3.6 > 200) return null;
  return {coordinates: [from, to], walking: false, warnings: []};
}
export function estimatedRouteConnection(previous: LocalRoutePoint | undefined, current: LocalRoutePoint) {
  return estimatedConnection(previous, current)?.coordinates ?? null;
}

// An explicit empty historical route must never fall back to today's tracker marker.
export function routeMapModel(position: LocalPosition | null, route?: LocalRoutePoint[], playbackIndex?: number) {
  const historical = route !== undefined;
  const visible = route === undefined ? [] : playbackIndex === undefined ? route : route.slice(0, Math.max(0, playbackIndex + 1));
  const displayed = historical ? visible.at(-1) ?? null : position;
  const model = trackerMapModel(displayed);
  const lines: Coordinate[][] = [], estimatedLines: Coordinate[][] = [];
  const estimatedWarnings = new Set<string>();
  let hasWalkingEstimate = false;
  let previous: LocalRoutePoint | undefined, interrupted = false;
  for (const point of visible) {
    const coordinate = trackerMapModel(point).point;
    if (!coordinate) { interrupted = true; continue; }
    if (!previous || !point.connectFromPrevious || interrupted) {
      lines.push([coordinate]);
      const connection = estimatedConnection(previous, interrupted ? { ...point, connectFromPrevious: false } : point);
      if (connection) {
        estimatedLines.push(connection.coordinates);
        if (connection.walking) hasWalkingEstimate = true;
        connection.warnings.forEach(warning => estimatedWarnings.add(warning));
      }
    } else lines.at(-1)!.push(coordinate);
    previous = point;
    interrupted = false;
  }
  if (visible.length > 1 && playbackIndex === undefined) {
    const coordinates = [...visible.map(point => trackerMapModel(point).point).filter((point): point is NonNullable<typeof point> => point !== null), ...estimatedLines.flat()];
    if (coordinates.length) {
      let minLat = 90, maxLat = -90;
      const longitudes = coordinates.map(point => { minLat = Math.min(minLat, point.latitude); maxLat = Math.max(maxLat, point.latitude); return (point.longitude + 360) % 360; }).sort((a, b) => a - b);
      let largestGap = -1, left = 0;
      for (let i = 0; i < longitudes.length; i++) {
        const gap = (i === longitudes.length - 1 ? longitudes[0] + 360 : longitudes[i + 1]) - longitudes[i];
        if (gap > largestGap) { largestGap = gap; left = longitudes[(i + 1) % longitudes.length]; }
      }
      const width = 360 - largestGap;
      model.region = { latitude: (minLat + maxLat) / 2, longitude: ((left + width / 2 + 180) % 360) - 180,
        latitudeDelta: Math.min(180, Math.max(0.006, (maxLat - minLat) * 1.35)), longitudeDelta: Math.min(360, Math.max(0.006, width * 1.35)) };
    }
  }
  return { ...model, displayed, historical, lines: lines.filter(line => line.length > 1), estimatedLines, hasWalkingEstimate, estimatedWarnings: [...estimatedWarnings] };
}

export function animateRouteModel(model:ReturnType<typeof routeMapModel>,route:LocalRoutePoint[]|undefined,index:number|undefined,fraction:number):ReturnType<typeof routeMapModel>{
 if(!route||index===undefined||fraction<=0||!route[index+1])return model;
 const from=trackerMapModel(route[index]).point,to=trackerMapModel(route[index+1]).point;if(!from||!to)return model;
 const direct=route[index+1].connectFromPrevious;
 const path=direct?[from,to]:estimatedRouteConnection(route[index],route[index+1]);
 const motion=interpolateRoutePath(path,fraction);if(!motion)return model;
 return {...model,point:motion.point,region:{...model.region,...motion.point},
   lines:direct?[...model.lines,motion.path]:model.lines,
   estimatedLines:direct?model.estimatedLines:[...model.estimatedLines,motion.path]};
}

// Frame the whole recording, including isolated fixes and straight gap connectors.
// Playback geometry changes on every frame; it must not move the user's camera.
export function routeFrameCoordinates(route:LocalRoutePoint[]) {
  return [...route.map(point=>trackerMapModel(point).point).filter((point):point is Coordinate=>point!==null),
    ...routeMapModel(null,route).estimatedLines.flat()];
}
